import React, { useContext, useEffect, useState, useRef } from 'react';
import { Alert, Button, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';
import moment from 'moment';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import AppContext from '../../../../context';
import {
  getStockPriceOnDate,
  fetchBSDTxs,
  updateExistingTxs,
} from '../../../../../api';
import DatePickerCustomized from '../../../add_transaction/stepone/date_picker_customized/date_picker_customized.component';
import Number from '../../../common/number';
import Loader from '../../../common/loader/loader.component';
import Dropdown from '../../../../common/dropdown/dropdown.component';
import './view_txs.component.css';

const ViewTxs = (props) => {
  // Context
  const context = useContext(AppContext);
  const portfolio_id = props.portfolioId;

  // State
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState({});
  const [meta, setMeta] = useState({});

  // isFetchPrice to prevent price fetch initially
  const isFetchPrice = useRef(true);
  const isInit = useRef(true);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(true);
  // current editable states
  const [editTxIdx, setEditTxIdx] = useState(null);
  const [date, setDate] = useState('');
  const [dateObj, setDateObj] = useState(null);
  const [type, setType] = useState('');
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('');
  // edited txs
  const [editedTxs, setEditedTxs] = useState([]);
  // type dropdown position
  const containerRef = useRef(null);
  const typeSelectRef = useRef(null);
  const [typeDropdownPos, setTypeDropdownPos] = useState({});
  // to enable save btn
  const [isEdited, setIsEdited] = useState(false);
  // error handling and loader to fetchBSDs
  const [areValidTxs, setAreValidTxs] = useState(true);
  const [errorMsg, setErrorMsg] = useState('Invalid sell transactions');
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);

  const [showError, setShowError] = useState(false);

  // Fetch B/S/D transactions for the company
  const fetchTransactions = async () => {
    try {
      const res = await fetchBSDTxs(
        portfolio_id,
        {
          fincode: props.fincode,
          transactions: [],
          is_staged: false,
          instrument_type: props.instrumentType,
        },
        'view',
      );
      const _txs = res.data.transactions;
      const _total = res.data.total;
      const _meta = res.data.meta;
      isInit.current = true;
      //disable delete when there is single tx
      const _bsTxs = _txs.filter((_tx) => ['BUY', 'SELL'].includes(_tx.type));
      if (_bsTxs.length === 1) {
        setIsDeleteEnabled(false);
      }
      setTxs([..._txs].reverse());
      setTotal(_total);
      setMeta(_meta);
      return true;
    } catch (error) {
      setErrorMsg('Something went wrong. Please try again');
      setAreValidTxs(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const getOffer = (tx) => {
    if (tx.type === 'BONUS' || tx.type === 'SPLIT') {
      return `${tx.ratio_1}:${tx.ratio_2}`;
    } else if (tx.type === 'DIVIDEND') {
      return `₹${tx.ratio_3} per share`;
    } else {
      return '-';
    }
  };

  const removeTx = async (tx) => {
    const tx_id = tx.tx_id;
    setShowError(false);
    setIsLoaderVisible(true);
    let clonedTxs = _.cloneDeep(txs);
    clonedTxs = clonedTxs.filter((_tx) =>
      ['BUY', 'REMOVE', 'SELL'].includes(_tx.type),
    );
    clonedTxs.find((_tx) => _tx.tx_id === tx_id).type = 'REMOVE';
    try {
      const res = await fetchBSDTxs(
        portfolio_id,
        {
          transactions: clonedTxs,
          fincode: meta.fincode,
          is_staged: true,
        },
        'edit',
      );
      const _txs = res.data.transactions;
      const _total = res.data.total;
      const _meta = res.data.meta;
      let bsd_txs = _txs.reverse();
      setTxs(bsd_txs);
      setTotal(_total);

      // add or update editedTx to editedTxs
      const clonedEditedTxs = _.cloneDeep(editedTxs);
      const idxInEditedTxs = clonedEditedTxs.findIndex(
        (tx) => tx.tx_id === tx_id,
      );
      if (idxInEditedTxs !== -1) {
        clonedEditedTxs[idxInEditedTxs].type = 'REMOVE';
      } else {
        clonedEditedTxs.push({ ...tx, type: 'REMOVE' });
      }
      setEditedTxs(clonedEditedTxs);
    } catch (error) {
      const errorMsg =
        error.response.status === 400
          ? 'Cannot remove the transaction: Results in negative amount of shares'
          : 'Something went wrong. Please try again';
      setErrorMsg(errorMsg);
      setAreValidTxs(false);
    }
    setIsLoaderVisible(false);
  };

  const close = async () => {
    props.handleClose();
  };

  const popover = (tx, id) => (
    <Popover>
      <Popover.Content>
        <div className="py-2">
          Are you sure you want to permanently delete this Transaction?
        </div>
        <div>
          <Button
            variant="danger"
            className="confirm-tx-btn"
            onClick={() => {
              removeTx(tx);
              document.body.click();
            }}
          >
            Delete
          </Button>
          <Button
            variant="secondary"
            className="confirm-tx-btn cancel-btn"
            onClick={() => document.body.click()}
          >
            Cancel
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  );

  const handleDateRawChange = (e) => {
    let dateStr = e.target.value;
    if (moment(dateStr, 'D-M-YYYY', true).isValid()) {
      dateStr = dateStr.split('-').reverse().join('-');
      let date_obj = new Date(dateStr);
      const minDate = new Date(meta.inception_date);
      if (date_obj > new Date()) {
        date_obj = new Date();
      }
      if (minDate && date_obj < minDate) {
        date_obj = minDate;
      }
      setDateObj(date_obj);
    }
  };

  // Form functions
  const positive_num = (v) => typeof v === 'number' && v > 0;
  const date_str = (v) => moment(v, 'D-M-YYYY', true).isValid();

  // Check is a value is valid based on a checker function(if provided)
  const isValid = ([val, checker]) => {
    if (val && val !== undefined && val !== '') {
      if (checker) return checker(val);
      return true;
    }
    return false;
  };

  const checkForm = () => {
    const values = [dateObj, type, shares, date, price];
    const validators = [null, null, positive_num, date_str, positive_num];
    const validations = values.map((v, i) => [v, validators[i]]);
    return validations.every(isValid);
  };

  const clearForm = () => {
    setDateObj(null);
    setDate('');
    setType('');
    setShares('');
    setPrice('');
    setAmount('');
  };

  const updateTx = async (txIdx) => {
    if (checkForm()) {
      const editedTx = { ...txs[txIdx] };
      editedTx.date = date;
      editedTx.shares = shares;
      editedTx.type = type;
      editedTx.price = price;
      editedTx.amount = amount;
      const isNotModified = Object.keys(editedTx).every((key) => {
        return editedTx[key] == txs[txIdx][key];
      });
      if (!isNotModified) {
        setIsLoaderVisible(true);
        let clonedTxs = _.cloneDeep(txs);
        clonedTxs[txIdx] = editedTx;
        clonedTxs = clonedTxs.filter((_tx) =>
          ['BUY', 'SELL', 'REMOVE'].includes(_tx.type),
        );
        clonedTxs = clonedTxs.map((_tx) => {
          const date_obj = new Date(_tx.date.split('-').reverse().join('-'));
          _tx['date_obj'] = date_obj;
          _tx['date'] = _tx.date;
          return _tx;
        });
        try {
          const res = await fetchBSDTxs(
            portfolio_id,
            {
              transactions: clonedTxs,
              fincode: meta.fincode,
              is_staged: true,
            },
            'edit',
          );
          const _txs = res.data.transactions;
          const _total = res.data.total;
          const _meta = res.data.meta;
          let bsd_txs = _txs.reverse();
          setTxs(bsd_txs);
          setTotal(_total);

          // add or update editedTx to editedTxs
          const clonedEditedTxs = _.cloneDeep(editedTxs);
          const idxInEditedTxs = clonedEditedTxs.findIndex(
            (tx) => tx.tx_id === editedTx.tx_id,
          );
          if (idxInEditedTxs !== -1) {
            clonedEditedTxs[idxInEditedTxs] = editedTx;
          } else {
            clonedEditedTxs.push(editedTx);
          }
          setEditedTxs(clonedEditedTxs);
        } catch (error) {
          const errorMsg =
            error.response.status === 400
              ? 'Invalid sell transaction: You do not have enough shares.'
              : 'Something went wrong. Please try again';
          setErrorMsg(errorMsg);
          setAreValidTxs(false);
        }
        setIsLoaderVisible(false);
      }
      setEditTxIdx(null);
    }
  };

  const getSetPrice = async (on_date) => {
    if (!props.fincode || !on_date) return;
    const res = await getStockPriceOnDate(props.fincode, on_date);
    const stock_price = res.data.data.value;
    if (stock_price) {
      setPrice(stock_price);
    }
  };

  const updateTxs = async () => {
    setAreValidTxs(true);
    setIsLoaderVisible(true);
    setIsEdited(false);
    try {
      const payload = [
        {
          company: meta,
          transactions: editedTxs,
        },
      ];
      const res = await updateExistingTxs(portfolio_id, payload);
      props.onEditTxs();
    } catch (error) {
      const errorMsg =
        error.response && error.response.status === 400
          ? 'Invalid transactions.'
          : 'Something went wrong. Please try again';
      setErrorMsg((error.response && error.response.data.message) || errorMsg);
      setAreValidTxs(false);
      setIsEdited(true);
    }
    setIsLoaderVisible(false);
  };

  const getSetDropdownsPosition = () => {
    let typeDropdownPos = {};
    const modalBody =
      containerRef.current.parentElement.getBoundingClientRect();
    // tx type input
    const typeInputPos = typeSelectRef.current.getBoundingClientRect();
    const typeInputWidth = typeInputPos.width;
    typeDropdownPos['width'] = typeInputWidth;
    typeDropdownPos['left'] = typeInputPos.left;

    const spaceAvail = modalBody.bottom - typeInputPos.bottom;
    spaceAvail > 100
      ? (typeDropdownPos['top'] = typeInputPos.bottom)
      : (typeDropdownPos['bottom'] =
          window.innerHeight - typeInputPos.top - 16);
    setTypeDropdownPos(typeDropdownPos);
  };

  useEffect(() => {
    clearForm();
    if (editTxIdx || editTxIdx === 0) {
      const tx = { ...txs[editTxIdx] };
      const date = tx.date.split('-').reverse().join('-');
      const date_obj = new Date(date);
      setDateObj(date_obj);
      setDate(tx.date);
      setType(tx.type);
      setShares(tx.shares);
      setPrice(tx.price);
      setAmount(tx.amount);
      getSetDropdownsPosition();
    }
  }, [editTxIdx]);

  useEffect(() => {
    if (dateObj && isFetchPrice.current) {
      getSetPrice(moment(dateObj).format('DDMMYYYY'));
    }
    isFetchPrice.current = true;
  }, [dateObj]);

  useEffect(() => {
    setAmount(((price || 0) * (shares || 0)).toFixed(2));
  }, [price, shares]);

  useEffect(() => {
    if (txs.length && !isInit.current) {
      !isEdited && setIsEdited(true);
    }
    isInit.current = false;
  }, [txs]);

  useEffect(() => {
    if (!areValidTxs) {
      setTimeout(() => setAreValidTxs(true), 4000);
    }
  }, [areValidTxs]);

  return (
    <div>
      <Modal
        show={props.show}
        onHide={() => props.handleClose()}
        centered={true}
        size="xl"
        className="view-tx-modal"
      >
        <Modal.Body className="view-tx-body p-md-5">
          <div className="view-tx-content" ref={containerRef}>
            <div id="calendar-portal"></div>
            <div className="view-tx-header">
              <span className="body-text">VIEW/EDIT TRANSACTIONS</span>
              <h5 className="body-heading pt-1">{meta.name}</h5>
            </div>
            <div className="pb-4 pt-3">
              <table className="summary-table table-responsive">
                <tbody>
                  <tr>
                    <th className="summary-table-header text-right pl-3">
                      ACTIVE SHARES
                    </th>
                    <th className="summary-table-header text-right pl-5">
                      TOTAL DIVIDEND
                    </th>
                  </tr>
                  <tr>
                    <td className="summary-table-data text-right">
                      {total.balance_shares}
                    </td>
                    <td className="summary-table-data text-right">
                      <Number value={total.dividend} usePrefix={true} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {meta.instrument_type === "mutual_funds" && total.show_notice && (
              <Alert variant={'warning'} show={true} className="mf-central-notice">
                Note: Transactions are sourced from MF Central, please notify in case of discrepancy. 
              </Alert>
            )}
            <Alert variant={'danger'} show={showError}>
              You are trying to remove a transaction which will result in a
              negative amount of shares. Please try and remove any leading Sell
              transactions.
            </Alert>
            <div className="position-relative">
              {!areValidTxs && (
                <div className="text-danger inline-error">{errorMsg}</div>
              )}
            </div>
            <div className="position-relative h-100 overflow-hidden">
              {isLoaderVisible && (
                <div className="overlay-loader-cont">
                  <Loader />
                </div>
              )}
              <div className="position-relative h-100 overflow-auto">
                <table className="table-responsive view-tx-table">
                  <tbody className="table-body position-relative">
                    <tr className="table-fixed-header">
                      <th className="nowrap pl-3 pr-3">Transaction Date</th>
                      <th className="nowrap pl-3 pr-3">Type</th>
                      <th className="nowrap pl-3 pr-3 text-right">Shares</th>
                      <th className="nowrap pl-3 pr-3 text-right">Price</th>
                      <th className="nowrap pl-3 pr-3 text-right">Offer</th>
                      <th className="nowrap pl-3 pr-3 text-right">Amount</th>
                      {meta.instrument_type !== "mutual_funds" && (
                        <th className="nowrap pl-3 pr-3 text-right">
                          Balance Shares
                        </th>
                      )}
                      {!context.is_sample && (
                        <th className="nowrap pl-3 pr-3 text-right">Actions</th>
                      )}
                    </tr>
                    {txs &&
                      txs.map(
                        (tx, idx_tx) =>
                          tx.type !== 'REMOVE' &&
                          (editTxIdx !== -1 && editTxIdx === idx_tx ? (
                            <tr className="table-row" key={'t-' + idx_tx}>
                              <td className="px-3 py-2">
                                <DatePickerCustomized
                                  selected={dateObj}
                                  onSelect={(d) => {
                                    setDateObj(d);
                                    setDate(moment(d).format('DD-MM-YYYY'));
                                  }}
                                  onChangeRaw={handleDateRawChange}
                                  type={type}
                                  minDate={meta.inception_date}
                                  isDisabled={false}
                                  inputClass="input-br bare"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Dropdown
                                  selected={type.toLowerCase()}
                                  setSelected={setType}
                                  placeholder="Type"
                                  options={['BUY', 'SELL']}
                                  containerClass="input-br select"
                                  ref={typeSelectRef}
                                  optionsPosition={typeDropdownPos}
                                />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <input
                                  className="w-100 text-right input-br bare"
                                  type="number"
                                  min={0}
                                  value={shares}
                                  placeholder="Shares"
                                  onChange={(e) => {
                                    setShares(parseInt(e.target.value));
                                  }}
                                ></input>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <input
                                  className="w-100 text-right input-br bare"
                                  min={0}
                                  type="number"
                                  step="0.01"
                                  placeholder="Share price"
                                  value={price}
                                  onChange={(e) => {
                                    const price = parseFloat(e.target.value);
                                    price && setPrice(price);
                                  }}
                                ></input>
                              </td>
                              <td className="px-3 py-2 text-right">{'-'}</td>
                              <td className="px-3 py-2 text-right">
                                <Number value={amount} usePrefix={true} />
                              </td>
                              {meta.instrument_type !== "mutual_funds" && (
                                <td className="px-3 py-2 text-right">
                                  {tx.balance}
                                </td>
                              )}
                              {!context.is_sample && (
                                <td className="px-3 py-2 text-right">
                                  <>
                                    <Button
                                      type="button"
                                      variant=""
                                      size="sm"
                                      className="inline-btn"
                                      onClick={() => updateTx(idx_tx)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      variant=""
                                      size="sm"
                                      onClick={() => setEditTxIdx(null)}
                                    >
                                      <FontAwesomeIcon
                                        icon={faTimes}
                                        size="sm"
                                        color="#dc3545"
                                      />
                                    </Button>
                                  </>
                                </td>
                              )}
                            </tr>
                          ) : (
                            <tr className="table-row" key={'t-' + idx_tx}>
                              <td className="nowrap px-3 py-2">{tx.date}</td>
                              <td className="nowrap px-3 py-2 text-capitalize">
                                {tx.type.toLowerCase()}
                              </td>
                              <td className="nowrap px-3 py-2 text-right">
                                {tx.shares}
                              </td>
                              <td className="nowrap px-3 py-2 text-right">
                                {tx.price}
                              </td>
                              <td className="nowrap px-3 py-2 text-right">
                                {getOffer(tx)}
                              </td>
                              <td className="nowrap px-3 py-2 text-right">
                                <Number value={tx.amount} usePrefix={true} />
                              </td>
                              {meta.instrument_type !== "mutual_funds" && (
                                <td className="nowrap px-3 py-2 text-right">
                                  {tx.balance}
                                </td>
                              )}
                              {!context.is_sample && (
                                <td className="nowrap px-3 py-2 text-right">
                                  {(tx.type == 'BUY' || tx.type == 'SELL') && (
                                    <>
                                      <Button
                                        type="button"
                                        variant=""
                                        size="sm"
                                        className="inline-btn"
                                        onClick={() => {
                                          isFetchPrice.current = false;
                                          setEditTxIdx(idx_tx);
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      {isDeleteEnabled ? (
                                        <OverlayTrigger
                                          trigger="click"
                                          placement="left"
                                          overlay={popover(tx, idx_tx)}
                                          rootClose
                                          className="shadow"
                                        >
                                          <Button
                                            variant=""
                                            size="sm"
                                            className="text-danger"
                                          >
                                            <Trash />
                                          </Button>
                                        </OverlayTrigger>
                                      ) : null}
                                    </>
                                  )}
                                </td>
                              )}
                            </tr>
                          )),
                      )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="modal-footer view-tx">
          {isEdited && (
            <>
              <span className="footer-text">
                Click on save to apply the changes to your portfolio
              </span>
              <Button
                size="sm"
                onClick={() => {
                  updateTxs();
                }}
                variant="success"
                className="modal-btn save"
              >
                Save
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={() => {
              close();
            }}
            variant="secondary"
            className="modal-btn cancel"
          >
            {isEdited ? 'Cancel' : 'Close'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewTxs;
